
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calculator, 
  TrendingUp, 
  Home, 
  MapPin, 
  BarChart3, 
  FileText, 
  MessageSquare, 
  ClipboardCheck,
  Building2,
  DollarSign
} from 'lucide-react';
import { fadeIn, staggerContainer, getReducedMotionVariants } from '../utils/motion';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';

interface ToolEcosystemPageProps {
  reducedMotion: boolean;
}

const ToolEcosystemPage: React.FC<ToolEcosystemPageProps> = ({ reducedMotion }) => {
  const toolCategories = [
    {
      category: "Analysis Tools",
      description: "Comprehensive financial analysis and mortgage calculations",
      tools: [
        {
          icon: Calculator,
          name: "Mortgage Calculator",
          description: "Advanced mortgage calculations with real-time scenarios",
          route: "/calculator"
        },
        {
          icon: TrendingUp,
          name: "SmartBuy Analyzer",
          description: "Compare two buying scenarios side-by-side",
          route: "/smartbuy-analyzer"
        },
        {
          icon: DollarSign,
          name: "Unified Buying Power",
          description: "Complete financial qualification analysis",
          route: "/unified-buying-power-calculator"
        }
      ]
    },
    {
      category: "Intelligence Tools",
      description: "Data-driven insights for smarter decisions",
      tools: [
        {
          icon: BarChart3,
          name: "Investor Analytics",
          description: "ROI calculations and investment projections",
          route: "/investor-analytics"
        },
        {
          icon: MapPin,
          name: "Community Intelligence",
          description: "Neighborhood data and market insights",
          route: "/community-intelligence"
        },
        {
          icon: Building2,
          name: "Property Tax Database",
          description: "Comprehensive property tax records and analysis",
          route: "/property-tax-database"
        }
      ]
    },
    {
      category: "Comparison & Documentation",
      description: "Tools to compare and prepare offers",
      tools: [
        {
          icon: Home,
          name: "Property Comparison",
          description: "Side-by-side property analysis",
          route: "/compare"
        },
        {
          icon: FileText,
          name: "Contract-Ready Forms",
          description: "Generate professional offer documentation",
          route: "/contract-ready-form"
        }
      ]
    },
    {
      category: "Support & Guidance",
      description: "AI-powered assistance and checklists",
      tools: [
        {
          icon: MessageSquare,
          name: "C.L.A.R.A. AI Assistant",
          description: "24/7 AI-powered homebuying guidance",
          route: "/chatbot"
        },
        {
          icon: ClipboardCheck,
          name: "Homebuyer Checklist",
          description: "Step-by-step homebuying process tracker",
          route: "/homebuyer-checklist"
        }
      ]
    }
  ];

  const platformStats = [
    { value: "10+", label: "Integrated Tools" },
    { value: "10,000+", label: "Analyses Completed" },
    { value: "99.9%", label: "Accuracy Rate" },
    { value: "24/7", label: "AI Support" }
  ];

  return (
    <div className="space-y-12">
      {/* Header Section */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4"
      >
        <h2 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
          Complete Tool Ecosystem
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Everything you need for confident homebuying decisions, all in one integrated platform
        </p>
      </motion.div>

      {/* Platform Stats */}
      <motion.div
        variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
      >
        {platformStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
          >
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Tool Categories */}
      {toolCategories.map((category, categoryIndex) => (
        <motion.div
          key={category.category}
          variants={getReducedMotionVariants(fadeIn('up', categoryIndex * 0.1, 0.5), reducedMotion)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="space-y-6"
        >
          <div className="text-center space-y-2">
            <h3 className="text-2xl font-bold">{category.category}</h3>
            <p className="text-muted-foreground">{category.description}</p>
          </div>

          <motion.div
            variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {category.tools.map((tool, toolIndex) => (
              <motion.div
                key={tool.name}
                variants={getReducedMotionVariants(fadeIn('up', toolIndex * 0.1, 0.4), reducedMotion)}
                whileHover={!reducedMotion ? { y: -8, transition: { type: "spring", stiffness: 300 } } : {}}
              >
                <Link to={tool.route} className="block h-full">
                  <Card className="h-full hover:border-primary/50 transition-all duration-300 group">
                    <CardContent className="p-6 space-y-4">
                      <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 group-hover:from-primary/30 group-hover:to-primary/20 transition-all duration-300">
                        <tool.icon className="w-7 h-7 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-lg group-hover:text-primary transition-colors">
                          {tool.name}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {tool.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      ))}

      {/* Coming Soon Section */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.5, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4 pt-8 border-t border-border"
      >
        <h3 className="text-xl font-semibold">Constantly Evolving</h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Our platform continuously grows with new tools and features based on user feedback and market needs.
          Stay tuned for upcoming integrations with lenders, real estate platforms, and advanced AI capabilities.
        </p>
      </motion.div>
    </div>
  );
};

export default ToolEcosystemPage;
